import React, { useEffect, useState } from "react";
import CustomTable from "../../../shared/custom-table/custom-table";
import { columnsRequests } from "../../../mocks/fakeData";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Grid,
  Button,
} from "@mui/material";
import {
  getAllTeacherRequests,
  respondOfferTeacherService,
  adjustTeacherOfferRequestService,
} from "../../../services/teacher-offer";

interface RequestData {
  id: number;
  nom: string; // Changé pour garantir que nom existe toujours
  offerType: string;
  status: "PENDING" | "ACCEPTED" | "REJECTED";
  createdAt: string;
  startDate: string | null;
  endDate: string | null;
  allowedClassNumber?: number;
  teacherOffer?: any;
  paymentImageUrl: string;
}

type TableData = Record<string, any>;

const Requests = () => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<RequestData | null>(null);
  const [data, setData] = useState<RequestData[]>([]);
  const [originalData, setOriginalData] = useState<RequestData[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<"ACCEPTED" | "REJECTED">("ACCEPTED");
  const [confirmAction, setConfirmAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [openAdjustDialog, setOpenAdjustDialog] = useState(false);
  const [adjustPayload, setAdjustPayload] = useState<{ allowedClassNumber: string; startDate: string; endDate: string }>({
    allowedClassNumber: "",
    startDate: "",
    endDate: "",
  });
  const [savingAdjust, setSavingAdjust] = useState(false);
  
  const [searchName, setSearchName] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState<"asc" | "desc" | "none">("none");

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getAllTeacherRequests();
      
      console.log("Données reçues de l'API:", res.data); // Debug
      
      const formattedData = res.data.map((item: any) => ({
        ...item,
        id: item.id,
        nom: item.superTeacherName || "Inconnu",
        offerType: item.teacherOffer?.title || "Type inconnu",
        status: item.status,
        createdAt: item.requestDate,
        startDate: item.startDate || null,
        endDate: item.endDate || null,
        allowedClassNumber: item.allowedClassNumber ?? item.teacherOffer?.classNumber ?? null,
        paymentImageUrl: item.paymentImageUrl || "#",
      }));
      
      console.log("Données formatées:", formattedData); // Debug
      setData(formattedData);
      setOriginalData(formattedData);
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (originalData.length === 0) return;

    let filteredData = [...originalData];

    // Filtre par nom
    if (searchName.trim()) {
      const searchTerm = searchName.toLowerCase().trim();
      filteredData = filteredData.filter(item => {
        const nom = item.nom.toLowerCase();
        return nom.includes(searchTerm);
      });
    }

    // Filtre par statut
    if (selectedStatusFilter !== "all") {
      filteredData = filteredData.filter(item => item.status === selectedStatusFilter);
    }

    // Tri par date
    if (dateFilter !== "none") {
      filteredData.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateFilter === "asc" 
          ? dateA.getTime() - dateB.getTime() 
          : dateB.getTime() - dateA.getTime();
      });
    }

    setData(filteredData);
  }, [searchName, selectedStatusFilter, dateFilter, originalData]);

  const handleResetFilters = () => {
    setSearchName("");
    setSelectedStatusFilter("all");
    setDateFilter("none");
    setData(originalData);
  };

  const handleActionClick = (
    row: RequestData,
    action: string,
    status: "ACCEPTED" | "REJECTED"
  ) => {
    setSelectedRow(row);
    setConfirmAction(action);
    setOpenConfirmDialog(true);
    setSelectedStatus(status);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setSelectedRow(null);
  };

  const handleOpenAdjustDialog = (row: RequestData) => {
    setSelectedRow(row);
    const todayStr = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);
    setAdjustPayload({
      allowedClassNumber: row.allowedClassNumber != null
        ? String(row.allowedClassNumber)
        : row.teacherOffer?.classNumber != null
          ? String(row.teacherOffer.classNumber)
          : "",
      startDate: row.startDate ? String(row.startDate).slice(0, 10) : todayStr,
      endDate: row.endDate ? String(row.endDate).slice(0, 10) : "",
    });
    setOpenAdjustDialog(true);
  };

  const handleCloseAdjustDialog = () => {
    setOpenAdjustDialog(false);
    setSelectedRow(null);
    setAdjustPayload({ allowedClassNumber: "", startDate: "", endDate: "" });
  };

  const handleActionConfirm = async () => {
    if (!selectedRow) return;
    
    try {
      await respondOfferTeacherService(selectedRow.id, selectedStatus);
      await fetchData();
      setOpenConfirmDialog(false);
      setSelectedRow(null);
    } catch (e) {
      console.error("Error responding to offer:", e);
    }
  };

  const handleAdjustConfirm = async () => {
    if (!selectedRow) return;
    const allowedValue = Number(adjustPayload.allowedClassNumber);
    if (!Number.isFinite(allowedValue) || allowedValue <= 0) {
      alert("Veuillez saisir un nombre de groupes valide.");
      return;
    }
    if (!adjustPayload.startDate || !adjustPayload.endDate) {
      alert("Veuillez renseigner les dates de debut et de fin.");
      return;
    }
    if (new Date(adjustPayload.endDate) < new Date(adjustPayload.startDate)) {
      alert("La date de fin doit etre posterieure a la date de debut.");
      return;
    }
    try {
      setSavingAdjust(true);
      await adjustTeacherOfferRequestService(selectedRow.id, {
        allowedClassNumber: allowedValue,
        startDate: adjustPayload.startDate,
        endDate: adjustPayload.endDate,
        status: "ACCEPTED",
      });
      await fetchData();
      handleCloseAdjustDialog();
    } catch (e) {
      console.error("Error updating request:", e);
    } finally {
      setSavingAdjust(false);
    }
  };

  const renderActions = (row: TableData) => {
    const requestRow = row as RequestData;
    return (
      <div className="flex items-center justify-center space-x-2">
        {requestRow.status === "PENDING" && (
          <>
            <button
              className="px-3 py-1 text-white rounded-full bg-primary_bg"
              onClick={() => handleActionClick(requestRow, "Accepted", "ACCEPTED")}
            >
              Accept
            </button>
            <button
              onClick={() => handleActionClick(requestRow, "Rejected", "REJECTED")}
              className="px-3 py-1 text-white rounded-full bg-red"
            >
              Reject
            </button>
          </>
        )}
        {requestRow.status === "ACCEPTED" && (
          <button
            className="px-3 py-1 text-white rounded-full bg-primary"
            onClick={() => handleOpenAdjustDialog(requestRow)}
          >
            Ajuster
          </button>
        )}
        {requestRow.status === "REJECTED" && (
          <span className="text-gray-500">
            Rejected
          </span>
        )}
      </div>
    );
  };

  const renderStatus = (row: TableData) => {
    const requestRow = row as RequestData;
    return (
      <div className="flex items-center justify-center space-x-2">
        <a
          className="px-3 py-1 text-white rounded-full bg-text"
          href={requestRow.paymentImageUrl}
          target="_blank"
          rel="noreferrer"
        >
          Voir le Doc
        </a>
      </div>
    );
  };

  return (
    <div className="w-full p-1 lg:p-10">
      <div className="flex flex-col items-start justify-between w-full mb-5 space-y-4  md:flex-row md:items-center md:space-y-0">
        <h1 className="text-2xl text-title font-montserrat_bold md:text-3xl">
          Les Demandes des prof
        </h1>
      </div>

      {/* Filtres */}
      <Box sx={{ mb: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Rechercher par nom"
              variant="outlined"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={selectedStatusFilter}
                label="Statut"
                onChange={(e) => setSelectedStatusFilter(e.target.value as string)}
              >
                <MenuItem value="all">Tous les statuts</MenuItem>
                <MenuItem value="PENDING">En attente</MenuItem>
                <MenuItem value="ACCEPTED">Accepté</MenuItem>
                <MenuItem value="REJECTED">Rejeté</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Tri par date</InputLabel>
              <Select
                value={dateFilter}
                label="Tri par date"
                onChange={(e) => setDateFilter(e.target.value as "asc" | "desc" | "none")}
              >
                <MenuItem value="none">Aucun tri</MenuItem>
                <MenuItem value="asc">Plus ancien</MenuItem>
                <MenuItem value="desc">Plus récent</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
           <Button
  fullWidth
  variant="contained"
  onClick={handleResetFilters}
  sx={{ 
    height: '56px',
    backgroundColor: '#4CAF50', // Vert standard
    '&:hover': {
      backgroundColor: '#388E3C', // Vert plus foncé au survol
    }
  }}
>
  Réinitialiser les filtres
</Button>
          </Grid>
        </Grid>
      </Box>

      {/* Contenu principal */}
      {loading ? (
        <div className="flex items-center justify-center h-64 ">
          <p className="text-lg text-gray-500">Chargement en cours...</p>
        </div>
      ) : data.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-lg text-gray-500">Aucune donnée disponible</p>
        </div>
      ) : (
        <CustomTable
          title="Gérer Demandes des prof"
          columns={columnsRequests}
          data={data}
          actions={renderActions}
          status={renderStatus}
        />
      )}

      <Dialog open={openAdjustDialog} onClose={handleCloseAdjustDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Ajuster l'offre enseignant</DialogTitle>
        <DialogContent className="space-y-4">
          <TextField
            fullWidth
            label="Nombre de groupes autorises"
            type="number"
            value={adjustPayload.allowedClassNumber}
            onChange={(e) =>
              setAdjustPayload((prev) => ({ ...prev, allowedClassNumber: e.target.value }))
            }
          />
          <TextField
            fullWidth
            label="Date de debut"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={adjustPayload.startDate}
            onChange={(e) =>
              setAdjustPayload((prev) => ({ ...prev, startDate: e.target.value }))
            }
          />
          <TextField
            fullWidth
            label="Date de fin"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={adjustPayload.endDate}
            onChange={(e) =>
              setAdjustPayload((prev) => ({ ...prev, endDate: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <button
            className="px-2 py-1 text-white rounded bg-red"
            onClick={handleCloseAdjustDialog}
            disabled={savingAdjust}
          >
            Annuler
          </button>
          <button
            className="px-2 py-1 text-white rounded bg-primary"
            onClick={handleAdjustConfirm}
            disabled={savingAdjust}
          >
            {savingAdjust ? "Mise a jour..." : "Sauvegarder"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de confirmation */}
      <Dialog open={openConfirmDialog} onClose={handleCloseConfirmDialog}>
        <DialogTitle>Confirmer</DialogTitle>
        <DialogContent>
          Êtes-vous sûr de vouloir {confirmAction.toLowerCase()} la demande de{" "}
          {selectedRow?.nom || "cet utilisateur"} ?
        </DialogContent>
        <DialogActions>
          <button
            className="px-2 py-1 text-white rounded bg-red"
            onClick={handleCloseConfirmDialog}
          >
            Annuler
          </button>
          <button
            className="px-2 py-1 text-white rounded bg-primary"
            onClick={handleActionConfirm}
          >
            {confirmAction}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Requests;
